import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, FileText, Mail, ShieldCheck } from "lucide-react";
import Header from "./Header";
import SiteFooter from "./SiteFooter";

const LEGAL_PAGES = [
  { path: "/privacy", label: "Privacy Policy", icon: ShieldCheck, desc: "How we collect, use and protect your data." },
  { path: "/terms", label: "Terms of Service", icon: FileText, desc: "The rules for using the MandapAI marketplace." },
];

const sectionId = (i) => `section-${i + 1}`;

// Rough reading time — 200 words per minute
const readingTime = (intro, sections) => {
  const text = [intro, ...sections.flatMap((s) => [s.heading, ...(Array.isArray(s.body) ? s.body : [s.body])])].join(" ");
  return Math.max(1, Math.round(text.split(/\s+/).filter(Boolean).length / 200));
};

/**
 * LegalDoc — shared layout for long-form policy pages (Privacy, Terms).
 *
 * Props
 *  - title:       page title
 *  - updated:     "last updated" string
 *  - intro:       optional lead paragraph
 *  - sections:    [{ heading, body: string | string[] }]
 */
const LegalDoc = ({ title, updated, intro, sections = [] }) => {
  const { pathname } = useLocation();
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Highlight the section currently in view in the table of contents
  useEffect(() => {
    const nodes = sections.map((_, i) => document.getElementById(sectionId(i))).filter(Boolean);
    if (!nodes.length || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveIdx(nodes.indexOf(visible[0].target));
      },
      { rootMargin: "-20% 0px -65% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sections]);

  const current = LEGAL_PAGES.find((page) => page.path === pathname);
  const other = LEGAL_PAGES.find((page) => page.path !== pathname);
  const HeroIcon = current?.icon || FileText;

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header overlay />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-pink-200/30 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-10 sm:pt-36 sm:pb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20">
              <HeroIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500">Legal</span>
          </div>
          <h1 className="font-['Manrope'] text-3xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
            {title}
          </h1>
          {intro && (
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-gray-600 leading-relaxed">{intro}</p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {updated && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600">
                <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
                Last updated {updated}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              {readingTime(intro, sections)} min read
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600">
              <FileText className="w-3.5 h-3.5 text-gray-400" />
              {sections.length} sections
            </span>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">

          {/* Table of contents — sticky on desktop */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">On this page</p>
              <ul className="border-l border-gray-200">
                {sections.map((s, i) => (
                  <li key={i}>
                    <a
                      href={`#${sectionId(i)}`}
                      className={`-ml-px block border-l-2 py-1.5 pl-4 text-sm transition-colors ${
                        activeIdx === i
                          ? "border-purple-600 font-semibold text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900"
                      }`}
                    >
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <div className="min-w-0">
            {/* Table of contents — chips on mobile */}
            <div className="lg:hidden -mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-1">
              {sections.map((s, i) => (
                <a
                  key={i}
                  href={`#${sectionId(i)}`}
                  className="whitespace-nowrap rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600"
                >
                  {i + 1}. {s.heading}
                </a>
              ))}
            </div>

            <div className="space-y-4">
              {sections.map((s, i) => (
                <section
                  key={i}
                  id={sectionId(i)}
                  className="scroll-mt-24 rounded-2xl border border-gray-200/70 bg-white p-5 sm:p-7 shadow-[0_1px_3px_rgba(16,24,40,0.04)]"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-50 border border-purple-100 font-['Manrope'] text-xs font-bold text-gray-700 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-['Manrope'] text-lg sm:text-xl font-bold text-gray-900">{s.heading}</h2>
                  </div>
                  {Array.isArray(s.body) ? (
                    <ul className="space-y-2.5">
                      {s.body.map((b, j) => (
                        <li key={j} className="flex gap-3 text-[15px] text-gray-600 leading-relaxed">
                          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[15px] text-gray-600 leading-relaxed">{s.body}</p>
                  )}
                </section>
              ))}
            </div>

            {/* Footer cards — contact + the other legal page */}
            <div className={`mt-6 grid gap-4 ${other ? "sm:grid-cols-2" : ""}`}>
              <div className="rounded-2xl border border-gray-200/70 bg-white p-5 sm:p-6 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <p className="mt-4 font-['Manrope'] text-base font-bold text-gray-900">Questions about this page?</p>
                <p className="mt-1 text-sm text-gray-500">Our team usually replies within a business day.</p>
                <a
                  href="mailto:support@mandapai.in"
                  className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all"
                >
                  <Mail className="w-4 h-4" />
                  support@mandapai.in
                </a>
              </div>

              {other && (
                <Link
                  to={other.path}
                  className="group rounded-2xl border border-gray-200/70 bg-white p-5 sm:p-6 shadow-[0_1px_3px_rgba(16,24,40,0.04)] hover:border-purple-200 hover:shadow-lg hover:shadow-purple-900/5 transition-all"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50">
                    <other.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="mt-4 font-['Manrope'] text-base font-bold text-gray-900">{other.label}</p>
                  <p className="mt-1 text-sm text-gray-500">{other.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                    Read it
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default LegalDoc;
